import { Check, ChevronDown } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import type { ObjectiveStatus } from "../api";
import { STATUS_BY_KEY, STATUS_META, STATUS_VARIANT, VARIANT_BG_CLASS } from "../constants";

interface StatusPillProps {
  value: ObjectiveStatus;
  onChange?: (value: ObjectiveStatus) => void;
  readOnly?: boolean;
}

export function StatusPill({ value, onChange, readOnly }: StatusPillProps) {
  const [open, setOpen] = useState(false);
  const meta = STATUS_BY_KEY[value];
  const variant = STATUS_VARIANT[value];
  const interactive = !readOnly && !!onChange;

  const trigger = (
    <Badge
      variant={variant}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-semibold",
        interactive ? "cursor-pointer" : "cursor-default",
      )}
    >
      <span className={cn("h-2 w-2 rounded-full", VARIANT_BG_CLASS[variant])} />
      {meta.label}
      {interactive && <ChevronDown className="h-3 w-3" />}
    </Badge>
  );

  if (!interactive) return trigger;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
        <button type="button" className="focus:outline-none">{trigger}</button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[180px] p-1" onClick={(e) => e.stopPropagation()}>
        {STATUS_META.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange?.(s.key);
              setOpen(false);
            }}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent"
          >
            <span className={cn("h-2.5 w-2.5 rounded-full", VARIANT_BG_CLASS[STATUS_VARIANT[s.key]])} />
            <span className="flex-1">{s.label}</span>
            {s.key === value && <Check className="h-3.5 w-3.5 text-foreground" />}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
